import {
	canOpenTenantMemberProfile,
	type DepartmentSummary,
	type MembershipSummary,
	type TenantInvitation,
	type TenantMemberSummary,
	type TenantRole,
} from '@livechat/shared';
import { type FormEvent, useEffect, useState } from 'react';

import {
	createAiAgent,
	getTenantAiSettings,
	inviteMember,
	listDepartments,
	listMembers,
} from '../api.js';
import { teamMemberRoleLabel } from '../member-label.js';
import { useDateFormat } from '../date-format.js';
import { MemberAvatar } from './MemberAvatar.js';

interface TenantMembersProps {
	embedded?: boolean;
	currentUserId: string;
	isPlatformAdmin?: boolean;
	membership: MembershipSummary;
	onOpenSettings: (userId: string) => void;
}

type CreatedInvitation = TenantInvitation;

function invitationUrl(invitation: TenantInvitation): string {
	const url = new URL(window.location.href);
	url.search = '';
	url.searchParams.set(
		invitation.kind === 'membership' ? 'membership' : 'setup',
		invitation.kind === 'membership' ? invitation.invitationToken : invitation.setupToken,
	);
	return url.toString();
}

export function TenantMembers({
	embedded = false,
	currentUserId,
	isPlatformAdmin = false,
	membership,
	onOpenSettings,
}: TenantMembersProps) {
	const { formatDateTime } = useDateFormat();
	const [members, setMembers] = useState<TenantMemberSummary[]>([]);
	const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
	const [departmentId, setDepartmentId] = useState('');
	const [presenceFilter, setPresenceFilter] = useState<'all' | 'online' | 'offline'>('all');
	const [loading, setLoading] = useState(true);
	const [showInvite, setShowInvite] = useState(false);
	const [busy, setBusy] = useState(false);
	const [memberType, setMemberType] = useState<'ai_agent' | 'human'>('human');
	const [aiAgentsEnabled, setAiAgentsEnabled] = useState(false);
	const [error, setError] = useState<string>();
	const [invitation, setInvitation] = useState<CreatedInvitation>();
	const canInvite = isPlatformAdmin || membership.role === 'owner' || membership.role === 'admin';

	useEffect(() => {
		setLoading(true);
		setError(undefined);
		void Promise.all([listMembers(membership.tenantId), listDepartments(membership.tenantId)])
			.then(([loadedMembers, loadedDepartments]) => {
				setMembers(loadedMembers);
				setDepartments(loadedDepartments);
				setDepartmentId('');
				setPresenceFilter('all');
			})
			.catch((caught: unknown) =>
				setError(caught instanceof Error ? caught.message : 'Could not load the team'),
			)
			.finally(() => setLoading(false));
	}, [membership.tenantId]);

	useEffect(() => {
		if (!canInvite) return;
		void getTenantAiSettings(membership.tenantId)
			.then((settings) => setAiAgentsEnabled(settings.aiAgentsEnabled))
			.catch(() => setAiAgentsEnabled(false));
	}, [canInvite, membership.tenantId]);

	async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		setBusy(true);
		setError(undefined);
		const form = event.currentTarget;
		const data = new FormData(form);

		try {
			if (memberType === 'ai_agent') {
				const created = await createAiAgent(membership.tenantId, {
					displayName: String(data.get('displayName')),
					email: String(data.get('email')),
				});
				setInvitation(undefined);
				setMembers(await listMembers(membership.tenantId));
				setShowInvite(false);
				form.reset();
				onOpenSettings(created.member.id);
				return;
			}
			const created = await inviteMember(membership.tenantId, {
				displayName: String(data.get('displayName')),
				email: String(data.get('email')),
				role: String(data.get('role')) as TenantRole,
			});
			setInvitation(created);
			setMembers(await listMembers(membership.tenantId));
			setShowInvite(false);
			form.reset();
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not invite the team member');
		} finally {
			setBusy(false);
		}
	}

	async function copySetupLink(): Promise<void> {
		if (invitation) {
			await navigator.clipboard.writeText(invitationUrl(invitation));
		}
	}

	function canOpenMember(member: TenantMemberSummary): boolean {
		return (
			isPlatformAdmin ||
			canOpenTenantMemberProfile(membership.role, member.role, member.id === currentUserId)
		);
	}

	function memberDepartments(userId: string): DepartmentSummary[] {
		return departments.filter((department) =>
			department.members.some((member) => member.userId === userId),
		);
	}

	const departmentMembers = departmentId
		? members.filter((member) =>
				departments
					.find((department) => department.id === departmentId)
					?.members.some((assigned) => assigned.userId === member.id),
			)
		: members;
	const visibleMembers = departmentMembers.filter((member) => {
		if (presenceFilter === 'online') return member.presenceStatus !== 'offline';
		if (presenceFilter === 'offline') return member.presenceStatus === 'offline';
		return true;
	});

	return (
		<section className={embedded ? undefined : 'workspace-section'}>
			<div className={embedded ? 'subsection-heading' : 'section-heading'}>
				<div>
					{embedded ? <h3>Team members</h3> : <h2>{membership.tenantName} team</h2>}
					<p className="muted">Manage the people who can administer and answer chats.</p>
				</div>
				<div className="member-heading-actions">
					<label className="member-department-filter">
						<select
							aria-label="Filter team members by department"
							onChange={(event) => setDepartmentId(event.currentTarget.value)}
							value={departmentId}
						>
							<option value="">All departments</option>
							{departments.map((department) => (
								<option key={department.id} value={department.id}>
									{department.name}
								</option>
							))}
						</select>
					</label>
					<label className="member-presence-filter">
						<select
							aria-label="Filter team members by availability"
							onChange={(event) =>
								setPresenceFilter(
									event.currentTarget.value as 'all' | 'online' | 'offline',
								)
							}
							value={presenceFilter}
						>
							<option value="all">All Agents</option>
							<option value="online">Online Agents</option>
							<option value="offline">Offline Agents</option>
						</select>
					</label>
					{canInvite && (
						<button
							className="primary-button"
							onClick={() => setShowInvite((visible) => !visible)}
						>
							{showInvite ? 'Cancel' : 'Add team member'}
						</button>
					)}
				</div>
			</div>

			{error && <div className="alert error">{error}</div>}
			{invitation && (
				<div className="setup-result">
					<div>
						<strong>Invitation created for {invitation.email}</strong>
						<p>
							{invitation.emailDelivery === 'queued'
								? 'The invitation email has been queued.'
								: 'Email delivery is not available, so send this link privately.'}{' '}
							{invitation.kind === 'membership'
								? 'They will sign in with their existing account to accept it. '
								: 'They will use it to create their account. '}
							It expires on {formatDateTime(invitation.expiresAt)}.
						</p>
					</div>
					<div className="setup-link-row">
						<input
							readOnly
							value={invitationUrl(invitation)}
							aria-label="Invitation link"
						/>
						<button className="secondary-button" onClick={() => void copySetupLink()}>
							Copy link
						</button>
					</div>
				</div>
			)}

			{showInvite && (
				<form className="inline-form member-form" onSubmit={(event) => void submit(event)}>
					<label>
						<span>Member type</span>
						<select
							onChange={(event) => {
								setMemberType(event.currentTarget.value as 'ai_agent' | 'human');
								setInvitation(undefined);
							}}
							value={memberType}
						>
							<option value="human">Regular</option>
							<option disabled={!aiAgentsEnabled} value="ai_agent">
								AI{aiAgentsEnabled ? '' : ' (enable in Settings → AI)'}
							</option>
						</select>
					</label>
					<label>
						<span>Name</span>
						<input name="displayName" required maxLength={160} />
					</label>
					<label>
						<span>Email address</span>
						<input
							name="email"
							type="email"
							required={memberType === 'human'}
							maxLength={320}
						/>
					</label>
					{memberType === 'human' ? (
						<label>
							<span>Role</span>
							<select name="role" defaultValue="agent">
								<option value="agent">Agent</option>
								<option value="supervisor">Supervisor</option>
								<option value="admin">Administrator</option>
								{isPlatformAdmin && <option value="owner">Owner</option>}
							</select>
						</label>
					) : (
						<label>
							<span>Role</span>
							<input disabled value="AI Agent" />
						</label>
					)}
					<button className="primary-button form-submit" disabled={busy}>
						{busy
							? 'Creating…'
							: memberType === 'ai_agent'
								? 'Create AI agent'
								: 'Create invitation'}
					</button>
				</form>
			)}

			<div className="table-card">
				<div className="table-head member-row standard-table-head">
					<span>Team member</span>
					<span>Department</span>
					<span>Role</span>
					<span>Status</span>
					<span>Chats</span>
				</div>
				{loading && <div className="empty-state">Loading team…</div>}
				{visibleMembers.map((member) => {
					const canOpen = canOpenMember(member);
					const assignedDepartments = memberDepartments(member.id);
					return (
						<div
							aria-label={
								canOpen ? `Open settings for ${member.displayName}` : undefined
							}
							className={`table-row member-row${canOpen ? ' member-row-link' : ''}`}
							key={member.id}
							onClick={canOpen ? () => onOpenSettings(member.id) : undefined}
							onKeyDown={
								canOpen
									? (event) => {
											if (event.key === 'Enter' || event.key === ' ') {
												event.preventDefault();
												onOpenSettings(member.id);
											}
										}
									: undefined
							}
							role={canOpen ? 'button' : undefined}
							tabIndex={canOpen ? 0 : undefined}
						>
							<div className="member-identity">
								<MemberAvatar
									dataUrl={
										member.avatarKind === 'uploaded'
											? member.avatarDataUrl
											: undefined
									}
									displayName={member.displayName}
									enabled={member.avatarEnabled}
									preset={member.avatarPreset}
									size="small"
								/>
								<div>
									<span className="member-name">{member.displayName}</span>
									{member.email && <span>{member.email}</span>}
								</div>
							</div>
							<span>
								{assignedDepartments.length
									? assignedDepartments
											.map((department) => department.name)
											.join(', ')
									: '—'}
							</span>
							<span className="capitalise">
								{teamMemberRoleLabel(member.memberType, member.role)}
							</span>
							<span className={`status-pill presence-${member.presenceStatus}`}>
								{member.presenceStatus}
							</span>
							<span>
								{member.activeChatCount} / {member.maxSimultaneousChats}
							</span>
						</div>
					);
				})}
				{!loading && !visibleMembers.length && (
					<div className="empty-state">No team members match these filters.</div>
				)}
			</div>
		</section>
	);
}
